import React, { useState } from "react";
import {
  Button,
  Grid,
  List,
  ListItem, 
  Typography,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; 
import { StyledButton } from "../../../../../platform/assets/css/styledComponent";
import { rootClientPath } from "../../../../../platform/util/link";
import { BookingType } from "../../../../../platform/types";
import {
  BookingTypeTitle,
  Circle,
} from "../../../../../platform/pages/business/userPage/userBookingTypePage.styles";

interface BookingTypeListProps {
  bookingTypes: BookingType[];
  category: number;
  maxBookingTypes?: number;
  primaryColor?: string;
  embeded?: boolean;
  showBookTypeColor?: boolean;
  ignoreDisplayOnBookingPageflag?: boolean;
  getBookingPath: (bookingType: BookingType) => string;
}

const BookingTypeListTemplate: React.FC<BookingTypeListProps> = ({
  bookingTypes,
  category,
  maxBookingTypes = 3,
  primaryColor,
  embeded = false,
  showBookTypeColor = false,
  ignoreDisplayOnBookingPageflag = false,
  getBookingPath,
}) => {
  const navigate = useNavigate();
  const [showAllBookingTypes, setShowAllBookingTypes] =
    useState<boolean>(false);

  const handleBookButtonClick = (bookingType: BookingType) => {
    const path = getBookingPath(bookingType);
    embeded
      ? window.open(`${rootClientPath}${path}`, "_blank")
      : navigate(path);
  };

  const handleViewAllClick = () => setShowAllBookingTypes(true);
  const handleCollapseClick = () => setShowAllBookingTypes(false);

  // Filter and categorize booking types based on category and visibility (isPublic)
  const filteredBookingTypes = bookingTypes
    .filter(
      (bookingType) =>
        (ignoreDisplayOnBookingPageflag || bookingType.displayOnBookingPage) &&
        Number(bookingType.bookingTypeCategoryId) === Number(category)
    )
    .slice(0, showAllBookingTypes ? undefined : maxBookingTypes);

  const totalFiltered = bookingTypes.filter(
    (bookingType) =>
      (ignoreDisplayOnBookingPageflag || bookingType.displayOnBookingPage) &&
      Number(bookingType.bookingTypeCategoryId) === Number(category)
  ).length;

  const showViewMoreButton =
    totalFiltered > maxBookingTypes && !showAllBookingTypes;

  return (
    <List>
      {filteredBookingTypes.map((bookingType) => (
        <React.Fragment key={bookingType.title}>
          <ListItem /* Optional: trim default gutters if you want tighter edges */
            // disableGutters
            sx={{ px: { xs: 1, sm: 2 } }}
          >
            <Grid
              container
              alignItems="center"
              // Avoid giant gaps that push content off-surface
              sx={{
                flexWrap: { xs: "wrap", md: "nowrap" }, // wrap on small, single row on md+
                columnGap: 2, // sane horizontal spacing
                rowGap: 1, // small vertical spacing when wrapping
                width: "100%",
              }}
            >
              {/* LEFT: Booking Type Info */}
              <Grid
                item
                xs
                // Let this side shrink so the right block stays inside the boundary
                sx={{
                  minWidth: 0, // critical for ellipsis to work in flex/grid
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {showBookTypeColor && (
                  <Circle style={{ background: bookingType.color }} />
                )}

                {/* Ensure long titles don't push the button off the surface */}
                <BookingTypeTitle
                  title={bookingType.title}
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {bookingType.title}
                </BookingTypeTitle>
              </Grid>

              {/* RIGHT: Price/Duration + Book button */}
              <Grid
                item
                // Keep this block compact and pinned to the right
                sx={{
                  ml: { xs: 0, md: "auto" }, // on md+ it sits at far right
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                  flexShrink: 0, // don't allow this block to shrink and break layout
                  // no maxWidth here; let contents define size without exceeding container
                }}
              >
                <Grid item sx={{ textAlign: "right", minWidth: 80 }}>
                  <Typography>
                    {bookingType.price ? `$${bookingType.price}` : "Free"}
                  </Typography>
                  <Typography noWrap>{`${bookingType.length} mins`}</Typography>
                </Grid>

                <Grid item>
                  <StyledButton
                    style={{
                      backgroundColor: primaryColor,
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleBookButtonClick(bookingType)}
                  >
                    Book
                  </StyledButton>
                </Grid>
              </Grid>
            </Grid>
          </ListItem>

          <Divider />
        </React.Fragment>
      ))}

      {/* View More / Collapse Buttons */}
      {showViewMoreButton && (
        <ListItem>
          <Button onClick={handleViewAllClick} variant="outlined">
            View all
          </Button>
        </ListItem>
      )}

      {showAllBookingTypes && (
        <ListItem>
          <Button onClick={handleCollapseClick} variant="outlined">
            Show less
          </Button>
        </ListItem>
      )}
    </List>
  );
};

export default BookingTypeListTemplate;
